import type { AxiosInstance } from 'axios';

import type { agents as a } from '@terra/contracts';

export type AgentStatus = 'ACTIVE' | 'INACTIVE';

export interface Agent {
  id: string;
  name: string;
  email: string;
  phone: string;
  alternatePhone: string | null;
  address: string;
  addressProof: string | null;
  cancelledCheque: string | null;
  agentCode: string;
  commissionBps: number;
  approved: boolean;
  status: AgentStatus;
  userId: string | null;
  createdAt: string;
  /** The agent's login and its KYC state; null for agents created before logins were linked. */
  user?: { id: string; kycStatus: string } | null;
  _count?: { leads: number };
}

export interface AgentPayoutSetting {
  payoutDueDate: string | null;
  payoutDayOfMonth: number | null;
  minPayoutPaise: number;
  status: AgentStatus;
  updatedAt: string;
}

export interface AgentPerformance {
  totalLeads: number;
  totalOrders: number;
  commissionBps: number;
  pendingPaise: number;
  inPayoutPaise: number;
  paidPaise: number;
  totalEarningsPaise: number;
  conversionRateBps: number;
}

export type AgentMe = a.AgentMe;
export type AgentCommissionRow = a.AgentCommissionRow;
export type AgentPayoutRow = a.AgentPayoutRow;
export type AdminAgentPayoutRow = a.AdminAgentPayoutRow;
export type AgentCustomerRow = a.AgentCustomerRow;
export type AgentPayoutStatus = a.AgentPayoutStatus;

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function agentEndpoints(http: AxiosInstance) {
  return {
    // ── admin ──
    list: (params?: { q?: string }) => unwrap<Agent[]>(http.get('/admin/agents', { params })),
    create: (body: {
      name: string;
      email: string;
      phone: string;
      alternatePhone?: string;
      address: string;
      addressProof?: string;
      cancelledCheque?: string;
      commissionBps?: number;
    }) => http.post<Agent>('/admin/agents', body).then((r) => r.data),
    update: (
      agentId: string,
      body: Partial<{
        name: string;
        phone: string;
        alternatePhone: string;
        address: string;
        commissionBps: number;
        approved: boolean;
        status: AgentStatus;
      }>,
    ) => http.patch<Agent>(`/admin/agents/${agentId}`, body).then((r) => r.data),
    remove: (agentId: string) => http.delete(`/admin/agents/${agentId}`).then((r) => r.data),
    getPayoutSetting: () =>
      http.get<AgentPayoutSetting>('/admin/agents/payout-setting').then((r) => r.data),
    updatePayoutSetting: (body: {
      payoutDueDate?: string;
      payoutDayOfMonth?: number | null;
      minPayoutPaise?: number;
      status: AgentStatus;
    }) => http.put<AgentPayoutSetting>('/admin/agents/payout-setting', body).then((r) => r.data),
    getPerformance: (agentId: string) =>
      http.get<AgentPerformance>(`/admin/agents/${agentId}/performance`).then((r) => r.data),
    getLeads: (agentId: string) => unwrap<any[]>(http.get(`/admin/agents/${agentId}/leads`)),
    getOrders: (agentId: string) => unwrap<any[]>(http.get(`/admin/agents/${agentId}/orders`)),
    getPayouts: (agentId: string) =>
      unwrap<AgentPayoutRow[]>(http.get(`/admin/agents/${agentId}/payouts`)),
    getAuditLogs: (agentId: string) => unwrap<any[]>(http.get(`/admin/agents/${agentId}/audit`)),

    /** Admin — every agent's payouts, with the bank or UPI to pay into. */
    listAllPayouts: (params?: { status?: AgentPayoutStatus }) =>
      unwrap<AdminAgentPayoutRow[]>(http.get('/admin/agents/payouts', { params })),
    /** Admin — create payouts now for every agent owed at least the minimum. */
    runPayouts: () =>
      http
        .post<{ created: number; skipped: number }>('/admin/agents/payouts/run')
        .then((r) => r.data),
    createPayoutForAgent: (agentId: string) =>
      http.post<AgentPayoutRow>(`/admin/agents/${agentId}/payouts`).then((r) => r.data),
    markPayoutPaid: (payoutId: string, body: { reference: string; note?: string }) =>
      http.post<AgentPayoutRow>(`/admin/agents/payouts/${payoutId}/paid`, body).then((r) => r.data),
    cancelPayout: (payoutId: string, body: { note: string }) =>
      http
        .post<AgentPayoutRow>(`/admin/agents/payouts/${payoutId}/cancel`, body)
        .then((r) => r.data),

    // ── agent ──
    me: () => http.get<AgentMe>('/agents/me').then((r) => r.data),
    myCommissions: (params?: { page?: number; pageSize?: number }) =>
      http
        .get<{
          data: AgentCommissionRow[];
          page: { page: number; pageSize: number; total: number; pageCount: number };
        }>('/agents/me/commissions', { params })
        .then((r) => r.data),
    myPayouts: () => unwrap<AgentPayoutRow[]>(http.get('/agents/me/payouts')),
    myCustomers: () => unwrap<AgentCustomerRow[]>(http.get('/agents/me/customers')),
    requestPayout: () => http.post<AgentPayoutRow>('/agents/me/payouts').then((r) => r.data),

    // ── customer ──
    /** Link the signed-in customer to the agent whose code brought them in. */
    attach: (code: string) =>
      http.post<{ ok: true; agentCode: string }>('/agents/attach', { code }).then((r) => r.data),
  };
}

export type AgentEndpoints = ReturnType<typeof agentEndpoints>;
